import { useCallback, useEffect, useRef, useState } from 'react';

import { LailaFaq, streamLailaChat } from '../../../api/ai';
import { LailaChatEvent } from '../../../api/ai/sse';
import { AIChatFile, LailaBlock, LailaCardBlock, LailaMessage } from '../../../types';
import { messageText } from '../utils';
import { CARD_KINDS } from '../components/cards/kinds';

let lastId = 0;
const nextId = () => `${Date.now()}-${++lastId}`;

const hasCard = (message: LailaMessage, id: string) =>
  message.blocks.some(block => block.type === 'card' && block.id === id);

const replaceCard = (message: LailaMessage, card: LailaCardBlock): LailaMessage =>
  hasCard(message, card.id)
    ? { ...message, blocks: message.blocks.map(b => (b.type === 'card' && b.id === card.id ? card : b)) }
    : message;

type CardData = LailaCardBlock['data'];

const findCard = (message: LailaMessage, id: string) =>
  message.blocks.find((block): block is LailaCardBlock => block.type === 'card' && block.id === id);

const removeCard = (message: LailaMessage, id: string): LailaMessage =>
  hasCard(message, id)
    ? { ...message, blocks: message.blocks.filter(b => !(b.type === 'card' && b.id === id)) }
    : message;

// A card the user has acted on for good, which a later version mustn't replace.
const SETTLED = ['sending', 'started', 'cancelled'];

// Where a card Laila sends again goes. It moves to the newest answer, so it's in
// view and in voice mode, unless the user already sent or cancelled the one
// with its id: that stays as it was, and the new one is a card of its own.
export const placeCard = (list: LailaMessage[], answerId: string, card: LailaCardBlock) => {
  let id = card.id;
  const settled = (candidate: string) =>
    list.some(m => SETTLED.includes(String(findCard(m, candidate)?.data?.state)));
  while (settled(id)) {
    id = `${id}~next`;
  }
  // The user's ticks live in data.local, which Laila never sends: keep them.
  const local = list.map(m => findCard(m, id)?.data?.local).find(Boolean);
  const data = local && !card.data.local ? { ...card.data, local } : card.data;
  return {
    list: list.map(m => (m.id === answerId ? m : removeCard(m, id))),
    card: { ...card, id, data },
  };
};

// The question as the model reads it, after anything it should know first.
export const withNotes = (question: string, notes: string[]) =>
  notes.length ? `[Since your last answer: ${notes.join(' ')}]\n\n${question}` : question;

const textMessage = (role: LailaMessage['role'], text: string, files?: AIChatFile[]): LailaMessage => ({
  id: nextId(),
  role,
  blocks: [{ type: 'text', text }],
  files: files?.length ? files : undefined,
  createdAt: Date.now(),
});

interface Options {
  instructions?: string;
  model?: string;
  // Searched on every question: the user's uploads plus any fixed stores (e.g. FAQ).
  vectorStoreIds: string[];
  // The help-centre FAQ for the user's role.
  faq?: LailaFaq;
  // Turns on Laila's time tools; the server runs them with the user's token.
  companyId?: string;
  // Voice mode: Laila also speaks her answer, in this voice.
  voice?: string;
  // Each spoken sentence of the answer, as it arrives.
  onAudio?: (audio: Extract<LailaChatEvent, { type: 'audio' }>) => void;
}

// The conversation with Laila: what the chat shows and one answer streaming
// in at a time. The conversation itself lives server-side: each question
// continues from the last answer's response id, so it is never resent.
export const useLailaChat = ({
  instructions,
  model,
  vectorStoreIds,
  faq,
  companyId,
  voice,
  onAudio,
}: Options) => {
  const [messages, setMessages] = useState<LailaMessage[]>([]);
  // True until the answer is complete, not just until it starts to show.
  const [thinking, setThinking] = useState(false);
  // What Laila is doing before the first words, e.g. "Checking who's clocked in".
  const [status, setStatus] = useState('');
  const previousResponseId = useRef<string>();
  // What the user did on a card since Laila's last answer, for her to know
  // with the next question.
  const notes = useRef<string[]>([]);
  const inFlight = useRef<AbortController | null>(null);
  const latestMessages = useRef(messages);
  latestMessages.current = messages;

  // The latest options, so a request started earlier still uses current stores.
  const options = useRef({ instructions, model, vectorStoreIds, faq, companyId, voice, onAudio });
  options.current = { instructions, model, vectorStoreIds, faq, companyId, voice, onAudio };

  const answer = useCallback(async (question: LailaMessage) => {
    const controller = new AbortController();
    inFlight.current = controller;
    setThinking(true);
    setStatus('');

    // The answer appears with its first words or card and grows with the rest:
    // its text first, then the cards its tools had to show.
    const answerId = nextId();
    let text = '';
    let cards: LailaCardBlock[] = [];
    const withAnswer = (list: LailaMessage[]): LailaMessage[] => {
      const blocks: LailaBlock[] = [...(text ? [{ type: 'text' as const, text }] : []), ...cards];
      return list.some(m => m.id === answerId)
        ? list.map(m => (m.id === answerId ? { ...m, blocks } : m))
        : [...list, { id: answerId, role: 'assistant', blocks, createdAt: Date.now() }];
    };
    const show = () => setMessages(withAnswer);

    let finished = false;
    try {
      const { instructions, model, vectorStoreIds, faq, companyId, voice } = options.current;
      await streamLailaChat(
        {
          input: withNotes(messageText(question), notes.current),
          previousResponseId: previousResponseId.current,
          instructions,
          model,
          vectorStoreIds,
          faq,
          companyId,
          voice,
          cards: [...CARD_KINDS],
          // Pieces of each sentence play as they are voiced, before all of it is.
          audioFormat: voice ? 'pcm' : undefined,
        },
        event => {
          if (controller.signal.aborted) {
            return;
          }
          switch (event.type) {
            case 'status':
              setStatus(event.label || '');
              break;
            case 'delta':
              text += event.text;
              setStatus('');
              show();
              break;
            case 'card': {
              const card: LailaCardBlock = {
                type: 'card',
                id: event.id,
                kind: event.kind,
                data: event.data,
              };
              setStatus('');
              setMessages(list => {
                const placed = placeCard(list, answerId, card);
                cards = cards.some(c => c.id === placed.card.id)
                  ? cards.map(c => (c.id === placed.card.id ? placed.card : c))
                  : [...cards, placed.card];
                return withAnswer(placed.list);
              });
              break;
            }
            case 'audio':
              options.current.onAudio?.(event);
              break;
            case 'done':
              finished = true;
              previousResponseId.current = event.responseId;
              text = event.text || text;
              show();
              break;
            case 'error':
              throw new Error(event.message);
          }
        },
        controller.signal
      );
      if (!finished) {
        throw new Error('Laila stopped mid-answer');
      }
      notes.current = [];
    } catch (e) {
      // A stopped answer keeps what was shown; a failed one is taken back and
      // the question offers a retry, which continues from the same point.
      if (!controller.signal.aborted) {
        setMessages(list =>
          list.filter(m => m.id !== answerId).map(m => (m.id === question.id ? { ...m, failed: true } : m))
        );
      }
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setThinking(false);
        setStatus('');
      }
    }
  }, []);

  // Returns false when the question wasn't sent: empty, or another one in flight.
  const send = useCallback(
    (text: string, files?: AIChatFile[]) => {
      if (inFlight.current || !text.trim()) {
        return false;
      }
      const question = textMessage('user', text, files);
      // An earlier failed question stays in the chat; only the newest can be retried.
      setMessages(value => [...value.map(m => (m.failed ? { ...m, failed: false } : m)), question]);
      answer(question);
      return true;
    },
    [answer]
  );

  // Stable, so memoised bubbles don't re-render while an answer streams in.
  const retry = useCallback(() => {
    const list = latestMessages.current;
    const last = list[list.length - 1];
    if (inFlight.current || !last?.failed) {
      return;
    }
    setMessages(value => value.map(m => (m.id === last.id ? { ...m, failed: false } : m)));
    answer(last);
  }, [answer]);

  // Stops the answer; the server stops writing it too. What arrived stays shown,
  // but the next question continues from the last complete answer.
  const stop = useCallback(() => {
    const controller = inFlight.current;
    if (!controller) {
      return;
    }
    controller.abort();
    inFlight.current = null;
    setThinking(false);
    setStatus('');
  }, []);

  // A card the user acted on (picked, sent, cancelled) keeps its new data
  // where it is in the chat. Pass a function of the card's current data when
  // updates may overlap, as several resends finishing one after another do.
  const updateCard = useCallback((id: string, data: CardData | ((current: CardData) => CardData)) => {
    setMessages(list =>
      list.map(m => {
        const card = findCard(m, id);
        return card
          ? replaceCard(m, { ...card, data: typeof data === 'function' ? data(card.data) : data })
          : m;
      })
    );
  }, []);

  // Tells Laila, with the next question, what the user did outside the chat.
  const noteForLaila = useCallback((note: string) => {
    notes.current = [...notes.current, note];
  }, []);

  useEffect(() => () => inFlight.current?.abort(), []);

  return { messages, thinking, status, send, retry, stop, updateCard, noteForLaila };
};
